"use client"

import { PromoIllustration } from "@/shared/components/PromotionCard/promo-illustration"
import { useClientTranslation } from "@/modules/App/components/i18n/i18n.client"
import { useDeletePet } from "@/modules/Quote/hooks/use-create-quote"
import { useSyncBack } from "@/modules/Quote/hooks/use-sync-back"
import { Button, ButtonDock, Header, MessageCard } from "@dalmatech/ui"
import { Pet } from "@/shared/types/global-store"
import { SyncBackBody } from "@/shared/types/lead-api-types"
import { ROUTES } from "@/shared/routing/routes"
import { newSubscriptionFlowService } from "@/store/machine"
import { useActor } from "@xstate/react"
import { useRouter } from "next/navigation"
import * as R from "ramda"
import React from "react"
import toast from "react-hot-toast"

import { AddAPetCard } from "../../components/AddAPetCard/add-a-pet-card"
import { TribeCard } from "../../components/TribeCard/tribe-card"
import * as styles from "./tribe.styled"

export const TribePage = () => {
  const [state, send] = useActor(newSubscriptionFlowService)
  const router = useRouter()
  const { t } = useClientTranslation("tribe-page")
  const [isLoading, setIsLoading] = React.useState(false)
  const { mutateAsync: syncBack, isPending: isSyncBackPending } = useSyncBack()
  const { mutateAsync: deletePetMutation, isPending: isDeletePetPending } = useDeletePet()
  const validPets = state.context.quote.pets?.filter((pet) => Boolean(pet.created_at))
  const hasMultiplePets = (validPets && validPets.length > 1) || false

  React.useEffect(() => {
    const allPets = state.context.quote?.pets
    if (!allPets || allPets.length === 0) return
    const persistedPets = allPets.filter((pet) => Boolean(pet.created_at))
    if (persistedPets.length === allPets.length) return
    send({
      type: "UPDATE_QUOTE",
      quote: {
        ...state.context.quote,
        pets: persistedPets,
      } as SyncBackBody,
    })
    send({
      type: "SET_CURRENT_PET_IDX",
      petIndex: null,
      petId: null,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const addNewPet = async () => {
    send("ADD_NEW_PET")
    return router.push(ROUTES.petName.path, { scroll: true })
  }

  const deletePet = async (petId?: Pet["id"] | null, petIdx?: Pet["idx"] | null) => {
    if (!petId || R.isNil(petIdx)) {
      return
    }
    try {
      const response = await deletePetMutation({
        quoteId: state.context.quote.meta?.quote_id || "",
        petIndex: petIdx,
      })
      if (response.success) {
        const petsClone = R.clone(state.context.quote.pets || [])
        const filteredPets = petsClone.filter((pet) => pet.id !== petId)
        const updatedPets = filteredPets.map((pet) => ({
          ...pet,
        }))
        const newQuote = {
          ...state.context.quote,
          pets: [...updatedPets],
        }
        await syncBack(newQuote)
        return send({
          type: "UPDATE_QUOTE",
          quote: newQuote as SyncBackBody,
        })
      }
      return toast.error("An error occurred. Please try again.")
    } catch (error) {
      return toast.error("An error occurred. Please try again.")
    }
  }

  const editPet = (petId: Pet["id"], petIdx: Pet["idx"]) => {
    send({
      type: "UPDATE_CURRENT_PET_IDX",
      petIndex: petIdx,
      petId,
    })
    router.push(ROUTES.petName.path, { scroll: true })
  }

  const validateTribe = () => {
    setIsLoading(true)
    router.push(ROUTES.attributionQuestion.path, { scroll: true })
  }

  if (!validPets) {
    return
  }

  return (
    <>
      <div className={styles.root(hasMultiplePets)}>
        <Header description={t("subtitle")}>{t("title")}</Header>
        <div className={styles.wrapper}>
          <div className={styles.cardsList}>
            {validPets?.map((pet) => (
              <TribeCard
                key={pet.idx}
                pet={pet as Pet}
                editPet={() => editPet(pet.id, pet.idx || 0)}
                deletePet={() => deletePet(pet.id, pet.idx)}
                hasMultiplePets={hasMultiplePets}
              />
            ))}
            <AddAPetCard onClick={addNewPet} />
          </div>

          <MessageCard
            variant="promotion"
            illustrationPosition="right"
            illustration={<PromoIllustration />}
            title={t("info-card.title")}
            description={t(
              validPets.length === 1
                ? "info-card.description-single"
                : "info-card.description-multiple"
            )}
          />
        </div>
        <ButtonDock isFixedOnMobile>
          <div className={styles.ctaWrapper(true)}>
            <Button
              type="button"
              size="lg"
              className="w-full"
              onClick={validateTribe}
              isLoading={isDeletePetPending || isSyncBackPending || isLoading}
            >
              {t("cta")}
            </Button>
          </div>
        </ButtonDock>
      </div>
    </>
  )
}
